import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const PrivacyPolicy = () => {
  const currentYear = new Date()?.getFullYear();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-900 hover:text-accent transition-colors">
            <Icon name="HardHat" size={24} className="text-accent" />
            <span className="font-semibold text-lg">ConstructionPro</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm text-gray-500">
            <Link to="/legal-terms" className="hover:text-gray-900 transition-colors">Legal</Link>
            <Link to="/cookie-policy" className="hover:text-gray-900 transition-colors">Cookie Policy</Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 md:p-12">
          {/* Title */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={20} className="text-accent" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            <p className="text-gray-500 text-sm">Last updated: January 1, 2025 &nbsp;&bull;&nbsp; Version 1.0</p>
          </div>

          <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
              <p>
                ConstructionPro (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
              </p>
              <p className="mt-3">
                Please read this policy carefully. If you disagree with its terms, please discontinue use of our platform.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
              <p className="mb-3">We collect information you provide directly to us, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, job title, and password when you register.</li>
                <li><strong>Profile Data:</strong> Profile photo, company affiliation, and professional details.</li>
                <li><strong>Project Data:</strong> Construction project details, bids, resources, and related business information you enter into the platform.</li>
                <li><strong>Usage Data:</strong> Log data, IP addresses, browser type, pages visited, and time spent on the platform.</li>
                <li><strong>Communications:</strong> Messages, support requests, and feedback you send us.</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, operate, and maintain the ConstructionPro platform</li>
                <li>Process transactions and send related information</li>
                <li>Send administrative information, updates, and security alerts</li>
                <li>Respond to comments, questions, and requests</li>
                <li>Monitor and analyze usage patterns to improve the platform</li>
                <li>Detect, prevent, and address technical issues and fraudulent activity</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Sharing of Information</h2>
              <p className="mb-3">We do not sell, trade, or rent your personal information. We may share information in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our platform (hosting, analytics, email delivery) under strict confidentiality agreements.</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with prior notice to you.</li>
                <li><strong>With Your Consent:</strong> In any other circumstances with your explicit consent.</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Retention</h2>
              <p>
                We retain your personal data for as long as your account is active or as needed to provide services. Upon account closure, personal data is deleted within 90 days, except where we are required to retain it for legal, regulatory, or legitimate business purposes (up to 7 years for financial records).
              </p>
            </section>

            {/* Security */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Security</h2>
              <p>
                We implement industry-standard security measures including encryption in transit (TLS), encryption at rest, access controls, and regular security audits. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Your Rights</h2>
              <p className="mb-3">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal obligations).</li>
                <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format.</li>
                <li><strong>Objection:</strong> Object to processing of your data for certain purposes.</li>
              </ul>
              <p className="mt-3">To exercise these rights, contact us at <a href="mailto:privacy@constructionpro.com" className="text-accent hover:underline">privacy@constructionpro.com</a>.</p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Cookies</h2>
              <p>
                We use cookies and similar tracking technologies to enhance your experience. For detailed information about our cookie practices, please review our{' '}
                <Link to="/cookie-policy" className="text-accent hover:underline">Cookie Policy</Link>.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the &quot;Last updated&quot; date. Continued use of the platform after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Contact Us</h2>
              <p className="mb-2">If you have questions about this Privacy Policy, please contact us:</p>
              <div className="space-y-1 text-sm">
                <p><strong>Email:</strong> <a href="mailto:privacy@constructionpro.com" className="text-accent hover:underline">privacy@constructionpro.com</a></p>
                <p><strong>Address:</strong> ConstructionPro, 123 Build Street, Suite 400, New York, NY 10001</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-8">
        <div className="max-w-4xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-500">
          <span>Copyright {currentYear} ConstructionPro. All rights reserved.</span>
          <nav className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-gray-900 transition-colors font-medium text-accent">Privacy Policy</Link>
            <span className="text-gray-300">|</span>
            <Link to="/legal-terms" className="hover:text-gray-900 transition-colors">Legal</Link>
            <span className="text-gray-300">|</span>
            <Link to="/cookie-policy" className="hover:text-gray-900 transition-colors">Cookie Policy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
